import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axiosInstance from "../../../configuration/request";
import { useSnackbar } from "../../../component/SnackbarContext";
import Breadcrumb from "../../../component/breadcrumb";
import CustomButton from "../../../component/elements/CustomButton/CustomButton";
import TextFieldCustom from "../../../component/elements/textfieldCustom";
import DatePickerCustom from "../../../component/elements/datePicker";
import SelectCustom from "../../../component/elements/selectCustom";
import MultiSelectCustom from "../../../component/elements/multiSelectCustom";
import { validateForm } from "../../../utils/formValidation/validation";
import { campaignRule } from "../../../utils/formValidation/validationRules";
import { formatDateToDDMMYYYY } from "../../../utils/dateFormat";
import {
  promotionTypes,
  serviceTypes,
  customerTags,
  campaignBreadcrumb as BCrumb,
  campaignVisibility,
  promotionTypeFields
} from "../../../constants/campaignConstants";
import dayjs from "dayjs";

export default function CreateCampaign() {
  const navigate = useNavigate();
  const showSnackbar = useSnackbar();
  const { loginDetails } = useSelector((state) => state.counter);
  const storeId = loginDetails.storeId || null;

  const [formData, setFormData] = useState({
    name: "",
    code: "",
    startDate: null,
    endDate: null,
    promotionType: "",
    xValue: "",
    yValue: "",
    zValue: "",
    service_type: '',
    customerTag: [],
    region: [],
    minimum_order_count: "",
    max_usage_count: "", // New State Field
    isPrivate: "Public",
  });

  const [formErrors, setFormErrors] = useState({});
  const requiredFields = promotionTypeFields[formData.promotionType] || [];
  const isXDisabled = !requiredFields.includes("xValue");
  const isYDisabled = !requiredFields.includes("yValue");
  const isZDisabled = !requiredFields.includes("zValue");

  const [location, setLocation] = useState([]);

  const offerSummary = useMemo(() => {
    const { promotionType, xValue, yValue, zValue } = formData;
    switch (promotionType) {
      case "flat_percent":
        return xValue ? `Flat ${xValue} % Off` : "";
      case "flat_dollar":
        return xValue ? `Flat $${xValue} Off` : "";
      case "buy_x_get_rs_y_off":
        return xValue && yValue ? `Buy Above ${xValue} unit/lb, Get $${yValue} Off` : "";
      case "buy_x_get_y_percent_upto_z":
        return xValue && yValue && zValue ? `Buy Above ${xValue} unit/lb, Get ${yValue}% Off Upto $${zValue}` : "";
      case "flat_percent_if_greater_than_x":
        return xValue && yValue ? `Flat ${yValue}% Off if unit/lb > $${xValue}` : "";
      default:
        return "";
    }
  }, [formData]);

  useEffect(() => {
    fetchServiceAreas();
  }, [storeId]);

  const fetchServiceAreas = async () => {
    if (!storeId) return;
    try {
      const response = await axiosInstance.get(`/customer/store/service-areas/${storeId}`);
      const data = response.data || [];
      const parsed = data.map((area) => ({
        value: area.routingNumber,
        label: area.routingNumber,
      }));
      setLocation(parsed);
    } catch (err) {
      console.error("Failed to fetch service areas", err);
    }
  };

  const handleChange = (field) => (valueOrEvent) => {
    const value = valueOrEvent?.target?.value ?? valueOrEvent;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
      ...(field === "promotionType" ? { xValue: "", yValue: "", zValue: "" } : {}),
    }));
    setFormErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleSubmit = () => {
    const errors = validateForm(formData, campaignRule(formData));
    setFormErrors(errors);
    if (Object.keys(errors).length === 0) {
      const payload = {
        name: formData.name,
        code: formData.code,
        startDate: formatDateToDDMMYYYY(formData.startDate),
        endDate: formatDateToDDMMYYYY(formData.endDate),
        promotionType: formData.promotionType,
        xValue: Number(formData.xValue) || 0,
        yValue: Number(formData.yValue) || 0,
        zValue: Number(formData.zValue) || 0,
        service_type: formData.service_type,
        customerTag: formData.customerTag,
        region: formData.region,
        minimum_order_count: Number(formData.minimum_order_count) || 0,
        storeId,
        status: "active",
        max_usage_count: Number(formData.max_usage_count) || null, // Sending 0 if empty
        isPrivate: formData.isPrivate === "Private",
      };
      axiosInstance
        .post(`/customer/store/campaign/create`, payload)
        .then(() => navigate("/app/promotions&marketing"))
        .catch((error) =>
          showSnackbar(error?.response?.data?.message || "Something went wrong!", "error")
        );
    }
  };

  return (
    <div className="flex flex-col px-8">
      <div className="mb-3 ml-4"><Breadcrumb title="" items={BCrumb} /></div>
      <div className="flex-1">
        <div className="w-full bg-white rounded-2xl shadow py-6 pr-4 pl-6 mb-4">
          <h2 className="text-xl font-semibold mb-4">Create Campaign</h2>
          <div className="overflow-auto h-[calc(100vh-335px)] pr-2">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 pt-2">
              <TextFieldCustom label="Campaign Name*" value={formData.name} onChange={handleChange("name")} error={!!formErrors.name} helperText={formErrors.name || " "} />
              <TextFieldCustom
                label="Promo Code*"
                value={formData.code}
                onChange={(e) => {
                  const transformedValue = e.target.value.toUpperCase().replace(/\s/g, "");
                  setFormData((prev) => ({
                    ...prev,
                    code: transformedValue,
                  }));
                  setFormErrors((prev) => ({ ...prev, code: "" }));
                }}
                error={!!formErrors.code}
                helperText={formErrors.code || " "}
              />
              <DatePickerCustom label="Start Date*" minDate={dayjs()} value={formData.startDate} onChange={handleChange("startDate")} error={!!formErrors.startDate} helperText={formErrors.startDate || " "} />
              <DatePickerCustom label="End Date*" minDate={dayjs()} value={formData.endDate} onChange={handleChange("endDate")} error={!!formErrors.endDate} helperText={formErrors.endDate || " "} />
              <SelectCustom label="Service Type*" options={serviceTypes} value={formData.service_type} onChange={handleChange("service_type")} error={!!formErrors.service_type} helperText={formErrors.service_type || " "} />
              <SelectCustom label="Campaign Visibility*" value={formData.isPrivate} onChange={handleChange("isPrivate")} options={campaignVisibility} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mt-6">
              <SelectCustom label="Promotion Type*" options={promotionTypes} value={formData.promotionType} onChange={handleChange("promotionType")} error={!!formErrors.promotionType} helperText={formErrors.promotionType || " "} />
              <TextFieldCustom label="X Value*" value={formData.xValue} onChange={(e) => { if (/^\d*$/.test(e.target.value)) handleChange("xValue")(e); }} disabled={isXDisabled} disableClass="custom-textfield-disable" error={!!formErrors.xValue} helperText={formErrors.xValue || " "} />
              <TextFieldCustom label="Y Value*" value={formData.yValue} onChange={(e) => { if (/^\d*$/.test(e.target.value)) handleChange("yValue")(e); }} disabled={isYDisabled} disableClass="custom-textfield-disable" error={!!formErrors.yValue} helperText={formErrors.yValue || " "} />
              <TextFieldCustom label="Z Value*" value={formData.zValue} onChange={(e) => { if (/^\d*$/.test(e.target.value)) handleChange("zValue")(e); }} disabled={isZDisabled} disableClass="custom-textfield-disable" error={!!formErrors.zValue} helperText={formErrors.zValue || " "} />
            </div>

            {offerSummary && <div className="mt-1 bg-[#3383B2] text-white text-center rounded-md py-3 text-sm font-medium">{offerSummary}</div>}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mt-6">
              <MultiSelectCustom label="Customer Tag" options={customerTags} value={formData.customerTag} onChange={handleChange("customerTag")} error={!!formErrors.customerTag} helperText={formErrors.customerTag || " "} />
              <MultiSelectCustom label="Region" options={location} value={formData.region} onChange={handleChange("region")} error={!!formErrors.region} helperText={formErrors.region || " "} />
              <TextFieldCustom label="Minimum Order Count" value={formData.minimum_order_count} onChange={(e) => { if (/^\d*$/.test(e.target.value)) handleChange("minimum_order_count")(e); }} error={!!formErrors.minimum_order_count} helperText={formErrors.minimum_order_count || " "} />
              <TextFieldCustom label="Max Usage Count" value={formData.max_usage_count} onChange={(e) => { if (/^\d*$/.test(e.target.value)) handleChange("max_usage_count")(e); }} error={!!formErrors.max_usage_count} helperText={formErrors.max_usage_count || " "} />
            </div>
          </div>
        </div>
      </div>
      <div className="sticky bottom-0 bg-white rounded-t-2xl shadow py-6 px-4 flex justify-center">
        <div className="flex gap-4">
          <CustomButton onClick={() => navigate("/app/promotions&marketing")} customeClass="primary-custom-button w-[150px] h-[45px]">Cancel</CustomButton>
          <CustomButton onClick={handleSubmit} customeClass="gradient-button w-[150px] h-[45px]">Submit</CustomButton>
        </div>
      </div>
    </div>
  );
}
